import Constants from "expo-constants";
import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from "@apollo/client/core";
import { map } from "rxjs/operators";
import { getAuth, getIdToken } from "../contexts/FirebaseAuth";
import { SetContextLink } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../graphql/activity";
import * as Sentry from "@sentry/react-native";
import { getDefaultStore } from "jotai";
import { createMaintenanceLink } from "./maintenance-link";
import { serverInMaintenanceAtom } from "../atoms/maintenance";

export const GRAPHQL_ENDPOINT = Constants.expoConfig.extra?.graphQLUrl;
const LOG_GRAPHQL_RESULT = false;

const httpLink = new HttpLink({
    uri: GRAPHQL_ENDPOINT,
});

const authLink = new SetContextLink(async ({ headers, liftProfile }) => {
    try {
        // get the authentication token from the user to be sure it is refreshed automatically
        const currentUser = getAuth().currentUser;
        const token = currentUser ? await getIdToken(currentUser) : null;
        // return the headers to the context so httpLink can read them
        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : "",
                ...(liftProfile
                    ? {
                          "Lift-Profile": liftProfile,
                      }
                    : {}),
            },
        };
    } catch (error) {
        Sentry.captureException(error);
        console.warn("[GraphQL AuthLink] Error when adding bearer token", error);
        // user will be signout automatically
        return { headers };
    }
});

const retryLink = new RetryLink({
    delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
    },
    attempts: {
        // An operation can opt out of the retries with `context: { skipRetry: true }`.
        retryIf: (error, operation) => !!error && !operation.getContext().skipRetry,
    },
});

// Drives the maintenance screen (see contexts/MaintenanceManager.tsx). It sits before the retryLink
// so that the screen only shows once the retries have failed too.
const maintenanceLink = createMaintenanceLink((isInMaintenance) => {
    getDefaultStore().set(serverInMaintenanceAtom, isInMaintenance);
});

const links = ApolloLink.from([
    authLink,
    createLogger(),
    createOmitTypenameLink(),
    maintenanceLink,
    retryLink,
    httpLink,
]);

const apolloCacheConfig = {
    typePolicies: {
        TimeSheet: {
            fields: {
                trackingDates: {
                    merge(existing, incoming) {
                        return incoming;
                    },
                },
            },
        },
        Query: {
            fields: {
                // Documentation for GraphQL pagination:
                // https://www.apollographql.com/docs/react/pagination/core-api
                findMissionAuditLogs: {
                    // Don't cache separate results based on
                    // any of this field's arguments.
                    keyArgs: false,

                    // Concatenate the incoming list items with
                    // the existing list items.
                    merge(existing, incoming, { args: { offset = 0 } }) {
                        // Slicing is necessary because the existing data is
                        // immutable, and frozen in development.
                        const elementsMerged = existing ? existing.elements.slice(0) : [];
                        for (let i = 0; i < incoming.elements.length; ++i) {
                            elementsMerged[offset + i] = incoming.elements[i];
                        }
                        const final = { ...incoming, elements: elementsMerged };
                        return final;
                    },
                },
                findMyInvoices: {
                    // Don't cache separate results based on
                    // any of this field's arguments.
                    keyArgs: false,

                    // Concatenate the incoming list items with
                    // the existing list items.
                    merge(existing, incoming, { args: { offset = 0 } }) {
                        // Slicing is necessary because the existing data is
                        // immutable, and frozen in development.
                        const elementsMerged = existing ? existing.elements.slice(0) : [];
                        for (let i = 0; i < incoming.elements.length; ++i) {
                            elementsMerged[offset + i] = incoming.elements[i];
                        }
                        const final = { ...incoming, elements: elementsMerged };
                        return final;
                    },
                },
                findMySalaries: {
                    // Don't cache separate results based on
                    // any of this field's arguments.
                    keyArgs: false,

                    // Concatenate the incoming list items with
                    // the existing list items.
                    merge(existing, incoming, { args: { offset = 0 } }) {
                        // Slicing is necessary because the existing data is
                        // immutable, and frozen in development.
                        const elementsMerged = existing ? existing.elements.slice(0) : [];
                        for (let i = 0; i < incoming.elements.length; ++i) {
                            elementsMerged[offset + i] = incoming.elements[i];
                        }
                        const final = { ...incoming, elements: elementsMerged };
                        return final;
                    },
                },
                getActivityTodoFeed: {
                    // activities can be removed from the backend
                    // so we always take the incoming list
                    merge(existing, incoming) {
                        return incoming;
                    },
                },
                getClientWorkers: {
                    merge(existing, incoming) {
                        return incoming;
                    },
                },
            },
        },
    },
};

// Factory so tests and Storybook can build a fresh cache with the same
// pagination `merge` policies as production.
export const createApolloCache = () => new InMemoryCache(apolloCacheConfig);

export const defaultApolloClientCache = createApolloCache();

export default new ApolloClient({
    link: links,
    cache: defaultApolloClientCache,
    defaultOptions: {
        watchQuery: {
            fetchPolicy: "cache-and-network",
        },
        mutate: {
            refetchQueries: [GET_ACTIVITY_TODO_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO],
        },
    },
});

// this method will filter the __typename that is added by apollo client. If we keep it we have error
// if we try to use this json to update a graphql object
// @see https://github.com/apollographql/apollo-client/issues/1564#issuecomment-357492659
function createOmitTypenameLink() {
    return new ApolloLink((operation, forward) => {
        if (operation.variables) {
            operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
        }

        return forward(operation);
    });
}

function createLogger() {
    return new ApolloLink((operation, forward) => {
        return forward(operation).pipe(
            map((result) => {
                if (LOG_GRAPHQL_RESULT) {
                    console.log("Apollo result: ", JSON.stringify(result));
                    if (result.data) {
                        console.log("   DATA: ", JSON.stringify(Object.keys(result.data)));
                    }
                }
                if (result.errors) {
                    console.log("Apollo errors: ", JSON.stringify(result.errors));
                }
                return result;
            }),
        );
    });
}

function omitTypename(key, value) {
    return key === "__typename" ? undefined : value;
}
